<template>
  <div class="shell-page purchase-status-page">
    <HeaderMain @messageFromHeader="searchEvents" />
    <SearchCourse v-if="!main" :courses="search" />

    <section class="purchase-status-section">
      <TicketAccessGate
        :prefill-email="$route.query.email || ''"
        title="Tus entradas viven detrás de un código por correo"
        subtitle="Usa el mismo correo de compra y te enviamos un código one-time para abrir tu historial, descargar archivos y revisar cada orden."
        @authenticated="loadTickets"
        @signed-out="resetTickets"
      >
        <template #default="{ session, logout }">
          <div class="purchase-status-card purchase-status-card--hero">
            <div class="purchase-status-hero">
              <div class="purchase-status-hero__copy">
                <span class="section-heading__eyebrow">Ticket wallet</span>
                <h2 class="section-heading__title">Tus accesos, en un solo lugar.</h2>
                <p class="section-heading__text">
                  Bienvenido<span v-if="session.first_name">, {{ session.first_name }}</span>. Aquí puedes revisar tus órdenes, descargar tus archivos y volver cuando necesites mostrar tu entrada.
                </p>
              </div>

              <div class="purchase-status-hero__actions">
                <router-link class="btn btn-outline-light liquid-page-button" :to="{ name: 'wallet' }">
                  <span class="liquid-page-button__glow"></span>
                  <span class="liquid-page-button__ring"></span>
                  <span class="liquid-page-button__inner">
                    <span class="liquid-page-button__copy">Abrir wallet</span>
                    <span class="liquid-page-button__icon"><i class="fas fa-wallet"></i></span>
                  </span>
                </router-link>
                <button class="btn btn-primary liquid-page-button" type="button" @click="logout">
                  <span class="liquid-page-button__glow"></span>
                  <span class="liquid-page-button__ring"></span>
                  <span class="liquid-page-button__inner">
                    <span class="liquid-page-button__copy">Cambiar correo</span>
                    <span class="liquid-page-button__icon"><i class="fas fa-rotate"></i></span>
                  </span>
                </button>
              </div>
            </div>

            <div class="purchase-status-highlights">
              <span>{{ ticketCount }} acceso{{ ticketCount === 1 ? '' : 's' }}</span>
              <span>{{ orders.length }} orden{{ orders.length === 1 ? '' : 'es' }}</span>
              <span>{{ session.email }}</span>
            </div>

            <div class="ticket-vault-grid">
              <article class="ticket-vault-card ticket-vault-card--spotlight">
                <div class="ticket-vault-card__copy">
                  <span class="ticket-vault-card__kicker">Ticket spotlight</span>
                  <h3>{{ primaryOrder ? primaryOrder.event_name : 'Tu primer acceso aparecerá aquí' }}</h3>
                  <p>{{ primaryOrder ? primaryOrder.venue_name : 'Valida tu correo para cargar tu vault de entradas.' }}</p>

                  <div class="ticket-vault-card__stats" v-if="primaryOrder">
                    <span>{{ formatDateShort(primaryOrder.event_date) }}</span>
                    <span>{{ itemCount(primaryOrder) }} ticket{{ itemCount(primaryOrder) === 1 ? '' : 's' }}</span>
                    <span>{{ primaryOrder.ticket_ready ? 'Listo para escanear' : 'Emisión en curso' }}</span>
                  </div>
                </div>

                <div class="ticket-vault-card__preview">
                  <div class="ticket-vault-card__preview-frame">
                    <qr-code :size="120" :text="qrTextForOrder(primaryOrder)" style="padding: 12px; background:#fff; border-radius:18px;"></qr-code>
                  </div>
                  <small>{{ primaryOrder ? 'Escanea o descarga tu archivo' : 'Tu QR aparecerá aquí' }}</small>
                </div>
              </article>

              <article class="ticket-vault-card ticket-vault-card--wallet-link">
                <div class="ticket-vault-card__copy">
                  <span class="ticket-vault-card__kicker">Wallet handoff</span>
                  <h3>Tu QR de consumo vive aparte</h3>
                  <p>Entradas y wallet ahora se sienten como dos objetos distintos: uno para entrar, otro para pagar o consumir rápido.</p>
                </div>

                <div class="ticket-vault-card__actions">
                  <router-link class="btn btn-outline-light liquid-page-button" :to="{ name: 'wallet' }">
                    <span class="liquid-page-button__glow"></span>
                    <span class="liquid-page-button__ring"></span>
                    <span class="liquid-page-button__inner">
                      <span class="liquid-page-button__copy">Ir al wallet</span>
                      <span class="liquid-page-button__icon"><i class="fas fa-arrow-right"></i></span>
                    </span>
                  </router-link>
                </div>
              </article>
            </div>

            <div v-if="loadingTickets" class="summary purchase-status-summary">
              <p>Buscando tus órdenes y archivos...</p>
            </div>

            <div v-else-if="ticketsError" class="summary purchase-status-summary">
              <p>{{ ticketsError }}</p>
            </div>

            <div v-else-if="!orders.length" class="summary purchase-status-summary">
              <p>No encontramos entradas asociadas a este correo todavía.</p>
            </div>

            <div v-else class="purchase-ticket-list">
              <article v-for="order in orders" :key="order.invoice_id" class="purchase-ticket-card">
                <div class="purchase-ticket-card__topline">
                  <span class="purchase-ticket-card__eyebrow">Orden #{{ order.invoice_id }}</span>
                  <span class="purchase-ticket-card__status" :class="{ 'is-ready': order.ticket_ready }">{{ order.ticket_ready ? 'Lista para mostrar' : 'Emisión en curso' }}</span>
                </div>

                <div class="purchase-ticket-card__board">
                  <div class="purchase-ticket-card__content">
                    <div class="purchase-ticket-card__hero">
                      <div>
                        <h3>{{ order.event_name }}</h3>
                        <p>{{ order.venue_name }}<span v-if="order.city_name"> - {{ order.city_name }}</span></p>
                      </div>

                      <div class="purchase-ticket-card__hero-meta">
                        <strong>{{ formatMoney(order.total) }}</strong>
                        <small>{{ formatDate(order.event_date) }}</small>
                      </div>
                    </div>

                    <div class="purchase-ticket-card__details-grid">
                      <div class="purchase-ticket-card__detail">
                        <span>Tipos</span>
                        <strong>{{ order.items.length }}</strong>
                      </div>
                      <div class="purchase-ticket-card__detail">
                        <span>Tickets</span>
                        <strong>{{ itemCount(order) }}</strong>
                      </div>
                      <div class="purchase-ticket-card__detail">
                        <span>Estado</span>
                        <strong>{{ order.ticket_ready ? 'Archivo listo' : 'Pendiente de emision' }}</strong>
                      </div>
                    </div>

                    <div class="purchase-ticket-card__items">
                      <div v-for="item in order.items" :key="item.id" class="purchase-ticket-card__item">
                        <div class="purchase-ticket-card__item-main">
                          <strong>{{ item.name }}</strong>
                          <small>{{ item.quantity }} ticket{{ item.quantity === 1 ? '' : 's' }}</small>
                        </div>

                        <div class="purchase-ticket-card__item-side">
                          <span v-if="item.holder_name">{{ item.holder_name }}</span>
                          <span v-if="item.seat_name">{{ item.seat_name }}</span>
                        </div>
                      </div>
                    </div>

                    <div class="purchase-ticket-card__actions">
                      <a v-if="order.ticket_ready && order.ticket_file_url" class="btn btn-primary liquid-page-button" :href="order.ticket_file_url" target="_blank" rel="noreferrer">
                        <span class="liquid-page-button__glow"></span>
                        <span class="liquid-page-button__ring"></span>
                        <span class="liquid-page-button__inner">
                          <span class="liquid-page-button__copy">Descargar archivo</span>
                          <span class="liquid-page-button__icon"><i class="fas fa-download"></i></span>
                        </span>
                      </a>
                      <a v-if="order.apple_wallet_url" class="btn btn-outline-light liquid-page-button" :href="order.apple_wallet_url" target="_blank" rel="noreferrer">
                        <span class="liquid-page-button__glow"></span>
                        <span class="liquid-page-button__ring"></span>
                        <span class="liquid-page-button__inner">
                          <span class="liquid-page-button__copy">Apple Wallet</span>
                          <span class="liquid-page-button__icon"><i class="fab fa-apple"></i></span>
                        </span>
                      </a>
                      <a v-if="order.google_wallet_url" class="btn btn-outline-light liquid-page-button" :href="order.google_wallet_url" target="_blank" rel="noreferrer">
                        <span class="liquid-page-button__glow"></span>
                        <span class="liquid-page-button__ring"></span>
                        <span class="liquid-page-button__inner">
                          <span class="liquid-page-button__copy">Google Wallet</span>
                          <span class="liquid-page-button__icon"><i class="fab fa-google"></i></span>
                        </span>
                      </a>
                      <span v-if="!order.ticket_ready" class="purchase-ticket-card__pending">Tu archivo se habilitara cuando la emision quede lista.</span>
                      <span v-else-if="!order.apple_wallet_url && !order.google_wallet_url" class="purchase-ticket-card__pending">Wallet passes listos para activarse cuando conectemos el emisor.</span>
                    </div>
                  </div>

                  <aside class="purchase-ticket-pass" :class="{ 'is-ready': order.ticket_ready }">
                    <div class="purchase-ticket-pass__surface">
                      <div class="purchase-ticket-pass__top">
                        <span class="purchase-ticket-pass__kicker">FOMO PASS</span>
                        <span class="purchase-ticket-pass__code">#{{ order.invoice_id }}</span>
                      </div>

                      <strong class="purchase-ticket-pass__event">{{ order.event_name }}</strong>
                      <div class="purchase-ticket-pass__subline">{{ formatDateShort(order.event_date) }}</div>

                      <div class="purchase-ticket-pass__qr-shell">
                        <qr-code :size="148" :text="qrTextForOrder(order)" style="padding: 14px; background:#fff; border-radius:22px;"></qr-code>
                      </div>

                      <div class="purchase-ticket-pass__footer">
                        <span>{{ primaryCodeLabel(order) }}</span>
                        <span>{{ order.ticket_ready ? 'Scan ready' : 'Preview' }}</span>
                      </div>
                    </div>
                  </aside>
                </div>
              </article>
            </div>
          </div>
        </template>
      </TicketAccessGate>
    </section>

    <FooterMain />
  </div>
</template>

<script>
import { apiServiceAuth } from '@/common/api.services'
import FooterMain from '@/components/FooterMain.vue'
import HeaderMain from '@/components/HeaderMain.vue'
import SearchCourse from '@/components/SearchCourse.vue'
import TicketAccessGate from '@/components/TicketAccessGate.vue'

export default {
  name: 'UserTicketView',

  components: {
    FooterMain,
    HeaderMain,
    SearchCourse,
    TicketAccessGate,
  },

  data() {
    return {
      search: [],
      searchField: '',
      main: true,
      loadingTickets: false,
      ticketsError: '',
      orders: [],
      ticketCount: 0,
    }
  },

  computed: {
    primaryOrder() {
      return this.orders[0] || null
    },
  },

  methods: {
    searchEvents(results, term) {
      this.search = results
      this.searchField = term
      this.main = term === ''
    },

    resetTickets() {
      this.orders = []
      this.ticketCount = 0
      this.ticketsError = ''
    },

    loadTickets() {
      this.loadingTickets = true
      this.ticketsError = ''

      apiServiceAuth('/api/my/tickets/').then((data) => {
        this.orders = data && Array.isArray(data.orders) ? data.orders : []
        this.ticketCount = data && data.ticket_count ? data.ticket_count : 0
      }).catch(() => {
        this.ticketsError = 'No pudimos cargar tus entradas en este momento.'
      }).finally(() => {
        this.loadingTickets = false
      })
    },

    formatMoney(value) {
      const amount = Number.parseFloat(value) || 0
      return `$${amount.toFixed(2)}`
    },

    formatDate(value) {
      if (!value) {
        return 'Fecha por confirmar'
      }

      return new Intl.DateTimeFormat('default', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(value))
    },

    formatDateShort(value) {
      if (!value) {
        return 'Fecha por confirmar'
      }

      return new Intl.DateTimeFormat('default', {
        dateStyle: 'medium',
      }).format(new Date(value))
    },

    itemCount(order) {
      return Array.isArray(order.items)
        ? order.items.reduce((sum, item) => sum + (item.quantity || 0), 0)
        : 0
    },

    qrTextForOrder(order) {
      if (!order) {
        return 'FOMO|TICKET|VAULT'
      }

      const primaryItem = Array.isArray(order.items) ? order.items.find((item) => item.access_code_snapshot) : null
      if (primaryItem && primaryItem.access_code_snapshot) {
        return `FOMO|TICKET|${primaryItem.access_code_snapshot}`
      }

      return `FOMO|TICKET|FOMO-${order.invoice_id}`
    },

    primaryCodeLabel(order) {
      const primaryItem = Array.isArray(order.items) ? order.items.find((item) => item.access_code_snapshot) : null
      const raw = ((primaryItem && primaryItem.access_code_snapshot) || `FOMO${order.invoice_id || '0000'}`).replace(/[^A-Z0-9]/gi, '').toUpperCase()
      const compact = raw.slice(-12).padStart(12, '0')
      return `FOMO-${compact.slice(0, 4)}-${compact.slice(4, 8)}-${compact.slice(8, 12)}`
    },
  },
}
</script>

<style scoped>
.ticket-vault-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-bottom: 18px;
}

.ticket-vault-card {
  display: grid;
  gap: 16px;
  padding: 20px;
  border-radius: 26px;
  background:
    radial-gradient(circle at top left, rgba(214, 221, 232, 0.08), transparent 28%),
    radial-gradient(circle at top right, rgba(98, 76, 144, 0.18), transparent 24%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.01)),
    rgba(9, 10, 15, 0.96);
  border: 1px solid rgba(201, 211, 231, 0.1);
  box-shadow: 0 22px 48px rgba(0, 0, 0, 0.24);
}

.ticket-vault-card__copy {
  display: grid;
  gap: 8px;
}

.ticket-vault-card__preview {
  display: grid;
  justify-items: start;
  gap: 10px;
}

.ticket-vault-card__preview-frame {
  padding: 10px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(201, 211, 231, 0.08);
}

.ticket-vault-card--spotlight {
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
}

.ticket-vault-card--wallet-link {
  align-content: space-between;
}

.ticket-vault-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.ticket-vault-card__kicker,
.purchase-ticket-card__eyebrow,
.purchase-ticket-pass__kicker {
  color: #cfd8ea;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.ticket-vault-card h3,
.purchase-ticket-card__top h3,
.purchase-ticket-pass__frame strong,
.purchase-ticket-card__amount strong {
  margin: 0;
  color: #fff;
}

.ticket-vault-card p,
.purchase-ticket-card__top p,
.purchase-ticket-card__item small,
.purchase-ticket-card__item span,
.purchase-ticket-card__amount small,
.purchase-ticket-card__pending,
.purchase-ticket-pass__frame small,
.purchase-ticket-pass__meta span {
  margin: 0;
  color: rgba(194, 204, 223, 0.8);
}

.ticket-vault-card__stats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.ticket-vault-card__stats span,
.purchase-ticket-card__status {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
  color: #dbe4f3;
  font-size: 0.72rem;
  font-weight: 700;
}

.purchase-ticket-card__status.is-ready {
  background: rgba(94, 151, 123, 0.18);
}

.purchase-ticket-list {
  display: grid;
  gap: 16px;
}

.purchase-ticket-card {
  display: grid;
  gap: 16px;
  padding: 20px;
  border-radius: 28px;
  background:
    radial-gradient(circle at top left, rgba(214, 221, 232, 0.07), transparent 26%),
    radial-gradient(circle at top right, rgba(98, 76, 144, 0.16), transparent 22%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.035), rgba(255, 255, 255, 0.01)),
    rgba(9, 10, 15, 0.95);
  border: 1px solid rgba(201, 211, 231, 0.1);
  box-shadow: 0 24px 54px rgba(0, 0, 0, 0.24);
}

.purchase-ticket-card__topline {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
}

.purchase-ticket-card__layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 310px;
  gap: 18px;
}

.purchase-ticket-card__content {
  display: grid;
  gap: 16px;
}

.purchase-ticket-card__hero {
  display: flex;
  justify-content: space-between;
  gap: 14px;
}

.purchase-ticket-card__hero-meta {
  display: grid;
  gap: 4px;
  justify-items: end;
  text-align: right;
}

.purchase-ticket-card__details-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.purchase-ticket-card__detail {
  display: grid;
  gap: 6px;
  padding: 12px 14px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(201, 211, 231, 0.06);
}

.purchase-ticket-card__detail span {
  color: rgba(194, 204, 223, 0.72);
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.purchase-ticket-card__detail strong {
  color: #fff;
  font-size: 0.96rem;
  line-height: 1.35;
}

.purchase-ticket-card__top,
.purchase-ticket-card__item,
.purchase-ticket-card__actions {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.purchase-ticket-card__top h3,
.purchase-ticket-card__top p,
.purchase-ticket-card__item strong,
.purchase-ticket-card__item small,
.purchase-ticket-card__item span,
.purchase-ticket-card__pending {
  margin: 0;
}

.purchase-ticket-pass {
  display: grid;
}

.purchase-ticket-pass__surface {
  position: relative;
  display: grid;
  gap: 12px;
  height: 100%;
  padding: 18px;
  border-radius: 28px;
  background:
    radial-gradient(circle at top left, rgba(214, 221, 232, 0.08), transparent 28%),
    radial-gradient(circle at top right, rgba(98, 76, 144, 0.18), transparent 24%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.01)),
    rgba(9, 10, 15, 0.96);
  border: 1px solid rgba(201, 211, 231, 0.1);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    0 0 0 1px rgba(255, 255, 255, 0.02);
}

.purchase-ticket-pass__surface::before,
.purchase-ticket-pass__surface::after {
  content: '';
  position: absolute;
  top: 50%;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #09090f;
  border: 1px solid rgba(201, 211, 231, 0.08);
  transform: translateY(-50%);
}

.purchase-ticket-pass__surface::before {
  left: -9px;
}

.purchase-ticket-pass__surface::after {
  right: -9px;
}

.purchase-ticket-pass__top,
.purchase-ticket-pass__footer {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
}

.purchase-ticket-pass__code,
.purchase-ticket-pass__subline,
.purchase-ticket-pass__footer span {
  color: rgba(194, 204, 223, 0.76);
  font-size: 0.78rem;
}

.purchase-ticket-pass__event {
  font-size: 1.08rem;
  line-height: 1.18;
}

.purchase-ticket-pass__subline {
  margin-top: -4px;
}

.purchase-ticket-pass__qr-shell {
  display: grid;
  justify-content: center;
  padding: 12px 0 8px;
}

.purchase-ticket-card__items {
  display: grid;
  gap: 10px;
}

.purchase-ticket-card__item {
  align-items: center;
  padding: 12px 14px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(201, 211, 231, 0.05);
}

.purchase-ticket-card__item-main,
.purchase-ticket-card__item-side {
  display: grid;
  gap: 4px;
}

.purchase-ticket-card__item-side {
  justify-items: end;
  text-align: right;
}

@media (max-width: 720px) {
  .ticket-vault-grid,
  .purchase-ticket-card__layout {
    grid-template-columns: 1fr;
  }

  .ticket-vault-card--spotlight,
  .purchase-ticket-card__hero,
  .purchase-ticket-pass__top,
  .purchase-ticket-pass__footer {
    display: grid;
  }

  .purchase-ticket-card__details-grid {
    grid-template-columns: 1fr;
  }

  .purchase-ticket-card__topline,
  .purchase-ticket-card__top,
  .purchase-ticket-card__item,
  .purchase-ticket-card__actions {
    display: grid;
  }

  .purchase-ticket-card__item-side,
  .purchase-ticket-card__hero-meta {
    justify-items: start;
    text-align: left;
  }
}
</style>
